import React from 'react';
import {HtmlFragment} from './HtmlFragment';
import {CodeBlock} from '@packages/systems/customCode/ui';

const generateFragment = (code: {
  siteLevel: CodeBlock[];
  pageLevel: CodeBlock[];
}) =>
  code.siteLevel
    .concat(code.pageLevel)
    .map(
      (codeEntry: CodeBlock) =>
        `<script src="${codeEntry.sourceUrl}"${
          codeEntry.customAttributes.length > 0
            ? ' ' +
              codeEntry.customAttributes
                .map(
                  ({name, value}: {name: string; value: string}) =>
                    `${name}="${value}"`
                )
                .join(' ')
            : ''
        }></script>`
    )
    .join('');

/**
 * NEVER use this component outside of the Canvas App as it can lead to XSS attacks.
 * This component allows to insert external custom code into the DOM. All the script tags will be inserted and executed.
 *
 * Currently used only for inserting custom code that is coming from Marketplace Apps.
 */
export const ExternalCustomCode = ({
  externalCustomCode,
  position,
  pageId,
  isReadyToBeInserted,
}: {
  externalCustomCode: CodeBlock[];
  position: 'header' | 'footer';
  pageId: string;
  isReadyToBeInserted: boolean;
}) => {
  const appliedCode = externalCustomCode.reduce(
    (acc, code) => {
      if (code.location !== position) return acc;

      if (code.target === 'Site') {
        acc.siteLevel.push(code);
      }

      if (code.target === 'Page' && code.targetId === pageId) {
        acc.pageLevel.push(code);
      }

      return acc;
    },
    {
      siteLevel: [] as CodeBlock[],
      pageLevel: [] as CodeBlock[],
    }
  );

  if (
    appliedCode.siteLevel.length === 0 &&
    appliedCode.pageLevel.length === 0
  ) {
    return null;
  }

  return (
    <HtmlFragment
      fragment={generateFragment(appliedCode)}
      isReadyToBeInserted={isReadyToBeInserted}
    />
  );
};
